import React, { useState, useEffect } from "react";
import { Modal, Button, Row, Col } from "react-bootstrap";
import moment from "moment";
import { getRequestChip, formatAmount } from "./helpers";

const formatDate = (value) => (value ? moment(value).format("DD MMM YYYY, h:mm A") : "—");

const ViewModal = ({ show, onHide, request, onEdit, onCancel, cancelling }) => {
  const [confirmingCancel, setConfirmingCancel] = useState(false);

  useEffect(() => {
    setConfirmingCancel(false);
  }, [request?.id, show]);

  if (!request) return null;

  const chip = getRequestChip(request);
  const isPending = request.status === "pending";

  return (
    <Modal show={show} onHide={onHide} centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>{request.reference_no}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {chip && (
          <span
            style={{
              display: "inline-block",
              background: chip.bg,
              color: chip.color,
              borderRadius: 999,
              padding: "5px 12px",
              fontSize: 12,
              fontWeight: 600,
              marginBottom: 16,
            }}
          >
            {chip.spinner && (
              <span
                className="spinner-border spinner-border-sm"
                style={{ width: 10, height: 10, marginRight: 6 }}
              />
            )}
            {chip.label}
          </span>
        )}

        <Row style={{ marginBottom: 12 }}>
          <Col xs={6}>
            <small style={{ color: "#6b7280" }}>Amount</small>
            <p style={{ margin: 0, fontWeight: 500 }}>{formatAmount(request.amount)}</p>
          </Col>
          <Col xs={6}>
            <small style={{ color: "#6b7280" }}>Requested</small>
            <p style={{ margin: 0, fontWeight: 500 }}>{formatDate(request.created_at)}</p>
          </Col>
        </Row>

        {request.reason && (
          <div style={{ marginBottom: 12 }}>
            <small style={{ color: "#6b7280" }}>Reason</small>
            <p style={{ margin: 0 }}>{request.reason}</p>
          </div>
        )}

        <div style={{ marginBottom: 12 }}>
          <small style={{ color: "#6b7280" }}>Requested By</small>
          <p style={{ margin: 0, fontWeight: 500 }}>{request.requested_by_name || "—"}</p>
        </div>

        {/* review_notes is always present on a rejection — surface it as the reason. */}
        {request.status === "rejected" && request.review_notes && (
          <div
            style={{
              marginBottom: 12,
              padding: "10px 12px",
              background: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: 8,
            }}
          >
            <small style={{ color: "#991b1b" }}>Rejection Reason</small>
            <p style={{ margin: "2px 0 0" }}>{request.review_notes}</p>
            {request.reviewed_by_name && (
              <p style={{ margin: "4px 0 0", fontSize: 11, color: "#9ca3af" }}>
                {request.reviewed_by_name} · {formatDate(request.reviewed_at)}
              </p>
            )}
          </div>
        )}

        {request.status !== "pending" && request.status !== "rejected" && request.reviewed_by_name && (
          <Row style={{ marginBottom: 12 }}>
            <Col xs={6}>
              <small style={{ color: "#6b7280" }}>Reviewed By</small>
              <p style={{ margin: 0, fontWeight: 500 }}>{request.reviewed_by_name}</p>
            </Col>
            <Col xs={6}>
              <small style={{ color: "#6b7280" }}>Reviewed</small>
              <p style={{ margin: 0, fontWeight: 500 }}>{formatDate(request.reviewed_at)}</p>
            </Col>
          </Row>
        )}

        {(request.status === "processing" || request.status === "funded" || request.status === "failed") && (
          <>
            <Row style={{ marginBottom: 12 }}>
              <Col xs={6}>
                <small style={{ color: "#6b7280" }}>Processed By</small>
                <p style={{ margin: 0, fontWeight: 500 }}>{request.processed_by_name || "—"}</p>
              </Col>
              <Col xs={6}>
                <small style={{ color: "#6b7280" }}>Processed</small>
                <p style={{ margin: 0, fontWeight: 500 }}>{formatDate(request.processed_at)}</p>
              </Col>
            </Row>
            {request.processing_notes && (
              <div style={{ marginBottom: 12 }}>
                <small style={{ color: "#6b7280" }}>Processing Notes</small>
                <p style={{ margin: 0 }}>{request.processing_notes}</p>
              </div>
            )}
          </>
        )}

        {request.status === "funded" && (
          <Row style={{ marginBottom: 12 }}>
            <Col xs={6}>
              <small style={{ color: "#6b7280" }}>Funding Reference</small>
              <p style={{ margin: 0, fontWeight: 500 }}>{request.funding_reference || "—"}</p>
            </Col>
            <Col xs={6}>
              <small style={{ color: "#6b7280" }}>Funded</small>
              <p style={{ margin: 0, fontWeight: 500 }}>{formatDate(request.funded_at)}</p>
            </Col>
          </Row>
        )}

        {/* A failed transfer isn't final — it goes back into the admin's payout
            queue and gets retried automatically, so this isn't a dead end. */}
        {request.status === "failed" && (
          <div
            style={{
              marginBottom: 12,
              padding: "10px 12px",
              background: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: 8,
            }}
          >
            <small style={{ color: "#991b1b" }}>Transfer Failed</small>
            <p style={{ margin: "2px 0 0" }}>
              {request.failure_reason || "The transfer could not be completed."}
            </p>
            <p style={{ margin: "4px 0 0", fontSize: 12, color: "#6b7280" }}>
              Finance has been notified — this will be retried, you don't need to submit a new request.
            </p>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer style={confirmingCancel ? { display: "block" } : undefined}>
        {isPending ? (
          confirmingCancel ? (
            <div>
              <div
                style={{
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: 8,
                  padding: "10px 12px",
                  marginBottom: 10,
                }}
              >
                <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#991b1b" }}>
                  Cancel this float request?
                </p>
                <p style={{ margin: "4px 0 0", fontSize: 12, color: "#7f1d1d" }}>
                  This can't be undone — you'll need to submit a new request if you still need the
                  float.
                </p>
              </div>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <Button
                  variant="light"
                  size="sm"
                  onClick={() => setConfirmingCancel(false)}
                  disabled={cancelling}
                >
                  Keep request
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => onCancel(request)}
                  disabled={cancelling}
                >
                  {cancelling ? "Cancelling..." : "Yes, cancel request"}
                </Button>
              </div>
            </div>
          ) : (
            <>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => setConfirmingCancel(true)}
                disabled={cancelling}
              >
                Cancel Request
              </Button>
              <Button variant="dark" size="sm" onClick={() => onEdit(request)}>
                Amend
              </Button>
            </>
          )
        ) : (
          <Button variant="light" size="sm" onClick={onHide}>
            Close
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ViewModal;
